<template>
  <div v-if="!appEmbedded" class="chart-tooltip">
    <!--  Tooltip content displayed on hover -->
    <transition name="fade">
      <div
        v-if="showTooltip && !displayFullscreen"
        class="chart-custom-tooltip"
        :class="tooltipClasses"
        :style="tooltipStyle"
        @click="handleCommentClick"
      >
        <div class="chart-custom-tooltip__title-hint-wrapper">
          <div
            v-if="title && title !== ''"
            class="chart-custom-tooltip__title"
          >
            {{ title }}
          </div>
          <div v-if="isClickable" class="chart-custom-tooltip__hint">
            {{ hint }}
          </div>
        </div>
        <EllipsisDiv :text="miniBodyHtml" class="chart-custom-tooltip__content" />
        <div v-if="footer && footer !== ''" class="chart-custom-tooltip__footer">
          {{ footer }}
        </div>
      </div>
    </transition>

    <!--  Content of the tooltip in a fullscreen modal -->
    <Modal v-model="displayFullscreen" class="chart-tooltip__modal">
      <div class="chart-tooltip__modal__content">
        <div class="legend">
          {{ title }}
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="bodyHtml" />
        <div class="chart-tooltip__modal__button h-v-centerer">
          <button
            class="generic-border generic-border--text-btn generic-border--cyan-border"
            @click="displayFullscreen = false"
          >
            {{ $t('message.OK') }}
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
/**
 * Tooltip of charts.
 * Usin a component was much more versatile.
 * Using following global events : CHART_TOOLTIP_SHOW, CHART_TOOLTIP_HIDE, CHART_TOOLTIP_SET_CLICKABLE
 */

import { Editor } from '@tiptap/vue-2';
import { CustomStarterKit } from '@/plugins/tiptap';
import $ from 'jquery';
// Components
import EllipsisDiv from '@/components/ellipsize.vue';
import Modal from '@/components/modal.vue';
// Utils
import { globalEventDispatcher, ShizenGlobalEvent } from '@/utils/event-dispatcher/global';
import { parsePointTooltipHtml } from '@/utils/indicator-utils';

export default {

  name: 'ChartTooltip',

  components: {
    EllipsisDiv,
    Modal,
  },

  data() {
    return {
      showTooltip: false,
      tooltipPosition: {},
      tooltipLeft: 0,
      tooltipTop: 0,
      title: null,
      hint: null,
      body: null,
      beforeBody: [],
      afterBody: [],
      footer: null,
      isClickable: false,
      displayFullscreen: false,
      editor: null,
    };
  },

  computed: {
    /** CSS style of the element */
    tooltipStyle() {
      return { ...this.tooltipPosition };
    },
    /** Object: Classes applied to the faded tooltip */
    tooltipClasses() {
      return {
        'chart-custom-tooltip--has-body': this.bodyHtml !== '',
      };
    },
    /**
     * String: HTML string to display when hovering a point
     * WARNING: If there are too many HTML elements to parse, this may slow Shizen a lot.
     * miniBodyHtml will be called as many times as the ChartTooltip is rebuilt,
     * which means a lot when hovering Chart.js points.
     * That's why, we only use the first three comments in the tooltip.
     * @return {String}
     */
    miniBodyHtml() {
      const { beforeBody, afterBody = [] } = this;
      return parsePointTooltipHtml({ beforeBody, afterBody: afterBody.slice(0, 3) }, this.body, false, true);
    },
    /** String: HTML string to display as the whole tooltip content */
    bodyHtml() {
      const { beforeBody, afterBody } = this;
      return parsePointTooltipHtml({ beforeBody, afterBody }, this.body, false, true);
    },
  },

  mounted() {
    const params = {
      content: '',
      extensions: [...CustomStarterKit],
    };
    this.editor = new Editor(params);
    globalEventDispatcher.register(ShizenGlobalEvent.CHART_TOOLTIP_SHOW, this.setupTooltip);
    globalEventDispatcher.register(ShizenGlobalEvent.CHART_TOOLTIP_HIDE, this.hideTooltip);
    globalEventDispatcher.register(ShizenGlobalEvent.CHART_TOOLTIP_SET_CLICKABLE, this.setClickable);
  },

  beforeDestroy() {
    globalEventDispatcher.unregister(ShizenGlobalEvent.CHART_TOOLTIP_SHOW, this.setupTooltip);
    globalEventDispatcher.unregister(ShizenGlobalEvent.CHART_TOOLTIP_HIDE, this.hideTooltip);
    globalEventDispatcher.unregister(ShizenGlobalEvent.CHART_TOOLTIP_SET_CLICKABLE, this.setClickable);
  },

  methods: {
    /** Setup and show the tooltip */
    setupTooltip(params) {
      const { tooltip, rect } = params;
      if (tooltip.body && tooltip.body.length) {
        this.title = tooltip.title instanceof Array ? tooltip.title.join('\n') : tooltip.title;
        this.body = tooltip.body instanceof Array ? `${tooltip.body[0].lines.join('\n')}` : tooltip.body;
        this.beforeBody = tooltip.beforeBody;
        this.afterBody = tooltip.afterBody;
        this.footer = tooltip.footer instanceof Array ? tooltip.footer.join('\n') : tooltip.footer;
      }
      // Tooltip position
      if (rect && tooltip && rect.top !== undefined) {
        // Position specified from Chart.js params
        this.tooltipPosition = {
          top: `${rect.top + tooltip.y}px`,
          left: `${rect.left + tooltip.x}px`,
          maxWidth: `${window.innerWidth - (rect.left + tooltip.x)}px`,
        };
      } else if (rect && rect.bottom !== undefined) {
        // Position specified from indicator.vue
        this.tooltipPosition = {
          bottom: `${rect.bottom}px`,
          right: `${rect.right}px`,
          width: `${rect.width}px`,
        };
      }
      this.showTooltip = true;
    },
    /** Hide the tooltip */
    hideTooltip() {
      this.showTooltip = false;
    },
    /** Set whether the tooltip is clickable to display a fullscreen version of the tooltip */
    setClickable({ clickable, canvas }) {
      if (clickable) {
        this.isClickable = true;
        this.hint = this.$t('message.CLICK_TOOLTIP_TO_ENLARGE');
        $(canvas).on('click.tooltip_click', this.handleCommentClick);
      } else {
        this.isClickable = false;
        $(canvas).off('click.tooltip_click', this.handleCommentClick);
      }
    },
    /** Handle click on a comment icon and on the canvas */
    handleCommentClick(evt) {
      if (this.isClickable) {
        evt.preventDefault();
        evt.stopPropagation();
        this.displayFullscreen = true;
      }
    },
  },
};
</script>

<style lang="scss">

.chart-tooltip {
  z-index: -1;
  pointer-events: none;

  > * {
    pointer-events: all;
  }

  &__modal.modal.dark-overlay {
    .dark-overlay-content {
      min-height: 0px;
      min-width: 0px;
      height: auto;
      width: auto;
      padding: 3em;
      text-align: center;
      box-sizing: border-box;
      text-align: left;
      max-width: 80%;
      max-height: 80%;
    }
  }

  &__modal {
    * {
      user-select: text;
    }

    &__content {
      min-width: 250px;
    }

    &__button {
      margin: 1rem 0 .3rem 0
    }
  }
}

// Chart custom tooltip
.chart-custom-tooltip {
  position: absolute;
  pointer-events: none;
  font-size: 0.95em;
  white-space: pre-wrap;
  border-radius: 5px;
  background: white;
  box-sizing: border-box;
  padding: 1rem;
  box-shadow: 0 3px 12px 1px rgba(0, 0, 0, 0.12);

  &--has-body {
    .chart-custom-tooltip__title-hint-wrapper {
      margin-bottom: .3rem;
    }
  }

  &__title-hint-wrapper {
    display: flex;
    overflow: hidden;
  }

  &__title {
    font-weight: 600;
    flex: 1;
    white-space: normal;
  }

  &__content {
    max-height: 12rem;
  }

  &__footer {
    opacity: 0.85;
    margin-top: 6px;
  }

  &__hint {
    margin-left: 1em;
    font-style: italic;
    flex: 0 0 auto;
    font-size: 0.9em;
    text-align: right;
    white-space: nowrap;
  }

  .chart-tooltip-mising {
    margin: 0.5em 0;

    .chart-tooltip-icon {
      width: 1.3em;
      height: 1.3em;
      fill: currentColor;
      color: white;
    }
  }
}
</style>
